export const PARTICIPANT_LAYOUT_REACTION_EVENT = "participant_layout_reaction";
export const PARTICIPANT_LAYOUT_REACTION_DATA_TYPE = "participant-layout-reaction-v1";

let participantLayoutReactionSequence = 0;
let syncRefCount = 0;
let subscribedConnection = null;
let removeListeners = null;

function getNafObject() {
  if (typeof NAF !== "undefined") {
    return NAF;
  }

  if (typeof window !== "undefined" && window.NAF) {
    return window.NAF;
  }

  if (typeof global !== "undefined" && global.NAF) {
    return global.NAF;
  }

  return null;
}

function isParticipantLayoutReactionPayload(reaction) {
  return Boolean(reaction?.participantId && reaction?.reactionId && reaction?.particleSrc);
}

export function dispatchParticipantLayoutReaction(reaction) {
  if (!isParticipantLayoutReactionPayload(reaction) || typeof window === "undefined") {
    return false;
  }

  window.dispatchEvent(new window.CustomEvent(PARTICIPANT_LAYOUT_REACTION_EVENT, { detail: reaction }));
  return true;
}

function broadcastParticipantLayoutReaction(reaction) {
  const connection = getNafObject()?.connection;

  if (!isParticipantLayoutReactionPayload(reaction) || !connection?.broadcastDataGuaranteed) {
    return false;
  }

  connection.broadcastDataGuaranteed(PARTICIPANT_LAYOUT_REACTION_DATA_TYPE, reaction);
  return true;
}

function onReceiveParticipantLayoutReaction(fromClientId, _dataType, reaction) {
  const naf = getNafObject();

  if (fromClientId && naf?.clientId && fromClientId === naf.clientId) {
    return;
  }

  dispatchParticipantLayoutReaction(reaction);
}

function registerConnectionSubscription() {
  const connection = getNafObject()?.connection;

  if (!connection?.subscribeToDataChannel) {
    return;
  }

  if (subscribedConnection === connection) {
    return;
  }

  subscribedConnection?.unsubscribeToDataChannel?.(PARTICIPANT_LAYOUT_REACTION_DATA_TYPE);
  connection.subscribeToDataChannel(PARTICIPANT_LAYOUT_REACTION_DATA_TYPE, onReceiveParticipantLayoutReaction);
  subscribedConnection = connection;
}

function addGlobalListeners() {
  if (removeListeners || typeof document === "undefined") {
    return;
  }

  const onConnected = () => {
    registerConnectionSubscription();
  };

  document.body?.addEventListener?.("connected", onConnected);

  removeListeners = () => {
    document.body?.removeEventListener?.("connected", onConnected);
    removeListeners = null;
  };
}

export function createParticipantLayoutReactionId() {
  participantLayoutReactionSequence += 1;
  return `participant-layout-reaction-${participantLayoutReactionSequence}`;
}

export function createParticipantLayoutReaction({ participantId, emoji }) {
  if (!participantId || !emoji) {
    return null;
  }

  return {
    participantId,
    reactionId: createParticipantLayoutReactionId(),
    emojiId: emoji.id,
    particleSrc: emoji.particleEmitterConfig?.src || emoji.src,
    icon: emoji.icon
  };
}

export function emitParticipantLayoutReaction({ participantId, emoji, broadcast = true }) {
  const reaction = createParticipantLayoutReaction({ participantId, emoji });

  if (!reaction) {
    return false;
  }

  const dispatched = dispatchParticipantLayoutReaction(reaction);

  if (broadcast) {
    broadcastParticipantLayoutReaction(reaction);
  }

  return dispatched;
}

export function ensureParticipantLayoutReactionSync() {
  syncRefCount += 1;

  registerConnectionSubscription();
  addGlobalListeners();

  return () => {
    syncRefCount = Math.max(0, syncRefCount - 1);

    if (syncRefCount === 0) {
      removeListeners?.();
      subscribedConnection?.unsubscribeToDataChannel?.(PARTICIPANT_LAYOUT_REACTION_DATA_TYPE);
      subscribedConnection = null;
    }
  };
}

export function resetParticipantLayoutReactionSyncForTests() {
  syncRefCount = 0;
  removeListeners?.();
  subscribedConnection?.unsubscribeToDataChannel?.(PARTICIPANT_LAYOUT_REACTION_DATA_TYPE);
  subscribedConnection = null;
}
